import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useFetchDocumentTypeForDocument } from "../../hooks/useDocumentTypes";
import {
  DocumentTypeInput,
  CreateDocumentData,
  UpdateDocumentData,
  Document,
  DocumentSourceReference,
  GenerationState,
  FileUploaderRef,
  DocumentResponse,
  UploadFileReference,
} from "../../types/documentTypes";
import FileUploader from "../common/FileUploader";
import DateInput from "../common/DateInput";
import Loading from "../../../../components/Loading";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useFeatureFlag } from "../../../../providers/FeatureFlagProvider";
import { useTranscripts } from "../../hooks/useTranscripts";
import { Transcript } from "../../types/transcripts";
import TranscriptSourcePicker from "./TranscriptSourcePicker";
import {
  canUploadSourceDocuments,
  getSectionRegenerationBlockedReason,
  shouldDisableGenerationActions,
} from "../../views/documentGeneration/generationLifecycle";

// Add interface for our form data
interface DocumentFormData {
  title: string;
  inputs: Record<string, string | number | Date>;
  sources: Record<string, File>;
}

interface ConfigPanelProps {
  documentTypeId: number;
  documentTypeVersion: number;
  documentId?: number;
  documentStatus?: Document["Status"];
  isGenerationActive: boolean;
  hasExistingSections?: boolean;
  isGenerating: boolean;
  isRegeneratingSection: boolean;
  generationState: GenerationState;
  onGenerate: (continueGeneration: boolean) => void;
  updateDocument: (data: UpdateDocumentData) => Promise<Document>;
  deleteSourceDocument: (
    sourceDocumentId: number,
    workspace_id: string,
  ) => Promise<void>;
  createDocument: (data: CreateDocumentData) => Promise<Document>;
  getDocument: (
    documentId: number,
    workspace_id: string,
  ) => Promise<DocumentResponse>;
  onDocumentUpdated?: (document: DocumentResponse) => void;
  onSourceDocumentCountChanged?: (count: number) => void;
  onSectionRegenerationBlockChange?: (reason: string) => void;
}

// Add this before the ConfigPanel component definition
const renderInput = (
  input: DocumentTypeInput,
  value: string,
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormData>>,
  disabled: boolean,
) => {
  const handleInputChange = (newValue: string) => {
    setFormData((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [input.Name]: newValue,
      },
    }));
  };

  switch (input.InputType) {
    case "Text":
      return (
        <Input
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          required={input.IsRequired}
          disabled={disabled}
          placeholder={`Enter ${input.Label.toLowerCase()}`}
        />
      );
    case "FreeText":
      return (
        <Textarea
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          required={input.IsRequired}
          disabled={disabled}
          minRows={3}
          placeholder={`Enter ${input.Label.toLowerCase()}`}
        />
      );
    case "Number":
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          required={input.IsRequired}
          disabled={disabled}
          placeholder={`Enter ${input.Label.toLowerCase()}`}
        />
      );
    case "Date":
      return (
        <DateInput
          value={value || ""}
          onChange={handleInputChange}
          required={input.IsRequired}
          disabled={disabled}
        />
      );
    case "Selection":
      const options = input.Options ? input.Options.split("|||") : [];
      return (
        <Select
          value={value}
          onChange={(_, newValue) => handleInputChange(newValue || "")}
          placeholder="Select..."
          required={input.IsRequired}
          disabled={disabled}
        >
          {options.map((option: string) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      );
    default:
      return null;
  }
};

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  documentTypeId: initialDocumentTypeId,
  documentTypeVersion,
  documentId,
  documentStatus,
  isGenerationActive,
  hasExistingSections = false,
  isGenerating,
  isRegeneratingSection,
  generationState,
  onGenerate,
  updateDocument,
  deleteSourceDocument,
  createDocument,
  getDocument,
  onDocumentUpdated,
  onSourceDocumentCountChanged,
  onSectionRegenerationBlockChange,
}) => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const authFetch = useAuthFetch();
  const transcriptsEnabled = useFeatureFlag("meridian_transcripts");
  const { listTranscripts } = useTranscripts();

  const [documentTypeId, setDocumentTypeId] = useState<number>(
    initialDocumentTypeId,
  );
  const {
    documentType,
    error: documentTypeError,
    loading: documentTypeLoading,
  } = useFetchDocumentTypeForDocument(
    documentTypeId,
    workspace?.id || "",
    documentTypeVersion,
  );

  // Consolidate state declarations
  const [formData, setFormData] = useState<DocumentFormData>({
    title: "",
    inputs: {},
    sources: {},
  });

  const [fileReferences, setFileReferences] = useState<
    Record<number, DocumentSourceReference[]>
  >({});
  const [availableTranscripts, setAvailableTranscripts] = useState<
    Transcript[]
  >([]);
  const [selectedTranscripts, setSelectedTranscripts] = useState<
    Record<number, Transcript | null>
  >({});
  const [transcriptSearch, setTranscriptSearch] = useState("");
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptsLoadError, setTranscriptsLoadError] = useState(false);
  const [transcriptsRetryCount, setTranscriptsRetryCount] = useState(0);
  const [transcriptSelectionSaving, setTranscriptSelectionSaving] = useState<
    Record<number, boolean>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [currentDocumentId] = useState<number | undefined>(documentId);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Tracks the window between clicking Generate/Regenerate and the generation
  // stream actually starting. Saving the document + refetching it are awaited
  // first, so without this the UI gives no feedback and invites double-clicks.
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);
  const [activeUploadsBySource, setActiveUploadsBySource] = useState<
    Record<number, boolean>
  >({});
  const [activeSourceDeletionCount, setActiveSourceDeletionCount] = useState(0);
  const [, _setLoadedDoc] = useState<Document | null>(null);
  const [isNewDocument] = useState(!documentId);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [documentLoading, setDocumentLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const hasActiveSourceDeletions = activeSourceDeletionCount > 0;
  const hasActiveTranscriptSelection = Object.values(
    transcriptSelectionSaving,
  ).some(Boolean);
  const sourceUploadEnabled = canUploadSourceDocuments({
    documentStatus,
    isGenerationActive,
    hasExistingSections,
    isGenerating,
    isStartingGeneration,
    isRegeneratingSection,
  });
  const formControlsEnabled =
    (sourceUploadEnabled || hasActiveSourceDeletions) &&
    !hasActiveTranscriptSelection;

  useEffect(() => {
    let mounted = true;
    if (!workspace?.id || !transcriptsEnabled) {
      setAvailableTranscripts([]);
      setTranscriptsLoading(false);
      return () => {
        mounted = false;
      };
    }
    setTranscriptsLoading(true);
    setTranscriptsLoadError(false);
    setAvailableTranscripts([]);
    listTranscripts(workspace.id, undefined, "Completed", transcriptSearch)
      .then((items) => {
        if (!mounted) return;
        const completedItems = items.filter(
          (item) => item.status === "Completed",
        );
        setAvailableTranscripts(completedItems);
        setSelectedTranscripts((current) => {
          const updated = { ...current };
          Object.entries(updated).forEach(([fieldId, selected]) => {
            if (!selected) return;
            const fresh = completedItems.find(
              (item) => item.transcript_id === selected.transcript_id,
            );
            if (fresh) updated[Number(fieldId)] = fresh;
          });
          return updated;
        });
      })
      .catch((loadError) => {
        if (mounted) {
          console.error("Failed to load completed transcripts", loadError);
          setAvailableTranscripts([]);
          setTranscriptsLoadError(true);
        }
      })
      .finally(() => {
        if (mounted) setTranscriptsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [
    listTranscripts,
    transcriptSearch,
    transcriptsEnabled,
    transcriptsRetryCount,
    workspace?.id,
  ]);

  const getSourceDocumentCount = useCallback(
    (references: Record<number, DocumentSourceReference[]>) =>
      Object.values(references)
        .filter((refs) => Array.isArray(refs))
        .reduce((total, refs) => total + refs.length, 0),
    [],
  );

  // Global loading: for new docs wait for document type; for existing, wait for both
  const isPanelLoading = isNewDocument
    ? documentTypeLoading
    : documentTypeLoading || documentLoading;

  // Add fileUploaderRef
  const fileUploaderRef = React.useRef<FileUploaderRef>(null);

  // Add error handling
  const handleUploadError = useCallback((error: string) => {
    console.error("File upload error:", error);
    // You can add toast notification or other error handling here
  }, []);

  const handleUploadActivityChange = useCallback(
    (sourceFieldId: number, isActive: boolean) => {
      setActiveUploadsBySource((prev) => {
        if (prev[sourceFieldId] === isActive) {
          return prev;
        }
        return { ...prev, [sourceFieldId]: isActive };
      });
    },
    [],
  );

  // Fix the type mismatch in handleFileReferenceReceived
  const handleFileReferenceReceived = useCallback(
    (sourceFieldId: number, fileRef: UploadFileReference) => {
      setFileReferences((prev) => {
        const existing = prev[sourceFieldId] || [];
        const refId = fileRef.id || 0;
        const definedFileRef = Object.fromEntries(
          Object.entries(fileRef).filter(([, value]) => value !== undefined),
        ) as Partial<DocumentSourceReference["fileReference"]>;
        if (refId) {
          const existingIndex = existing.findIndex(
            (reference) => reference.fileReference.id === refId,
          );
          if (existingIndex !== -1) {
            return {
              ...prev,
              [sourceFieldId]: existing.map((reference, index) =>
                index === existingIndex
                  ? {
                      fileReference: {
                        ...reference.fileReference,
                        ...definedFileRef,
                        id: refId,
                        isNew: reference.fileReference.isNew,
                      },
                    }
                  : reference,
              ),
            };
          }
        }
        return {
          ...prev,
          [sourceFieldId]: [
            ...existing,
            {
              fileReference: {
                originalName: fileRef.originalName,
                documentType: fileRef.documentType,
                uploadDate: fileRef.uploadDate,
                fileSize: fileRef.fileSize,
                mimeType: fileRef.mimeType,
                filePath: fileRef.filePath || "",
                sourceFieldId: fileRef.sourceFieldId,
                sourceName: fileRef.sourceName,
                indexStatus: fileRef.indexStatus,
                indexError: fileRef.indexError,
                isNew: false,
                id: refId,
              },
            },
          ],
        };
      });
    },
    [],
  );

  // Mirror the live source-document count to the parent. Skip the initial
  // empty-state fire so we don't overwrite a BE-provided count on mount.
  const hasReportedCountRef = useRef(false);
  useEffect(() => {
    if (!hasReportedCountRef.current) {
      hasReportedCountRef.current = true;
      return;
    }
    onSourceDocumentCountChanged?.(
      getSourceDocumentCount(fileReferences) +
        Object.values(selectedTranscripts).filter(Boolean).length,
    );
  }, [
    fileReferences,
    getSourceDocumentCount,
    onSourceDocumentCountChanged,
    selectedTranscripts,
  ]);

  // Consolidate document loading into a single useEffect
  useEffect(() => {
    let mounted = true;

    const loadDocument = async () => {
      if (!documentId || isNewDocument) return;

      try {
        setDocumentLoading(true);
        const doc = await getDocument(documentId, workspace?.id || "");

        if (!mounted) return;

        // Update document type ID only if it's different
        if (doc.DocumentType?.id && doc.DocumentType.id !== documentTypeId) {
          setDocumentTypeId(doc.DocumentType.id);
        }

        // Batch our state updates
        const refs: Record<number, DocumentSourceReference[]> = {};
        const selected: Record<number, Transcript | null> = {};
        if (doc.sourceDocuments) {
          doc.sourceDocuments.forEach((source) => {
            if (source.transcriptId && source.sourceFieldId) {
              selected[source.sourceFieldId] = {
                transcript_id: source.transcriptId,
                title: source.originalName,
                status: "Completed",
                visibility: "Private",
                language: "en-US",
                folder_id: null,
                folder_name: null,
                transcript_text: null,
                created_by_user_id: "",
                started_at: null,
                ended_at: null,
                duration_seconds: null,
                published_by_user_id: null,
                published_at: null,
                created_date: source.uploadDate,
                modified_date: source.uploadDate,
                segments: [],
              };
              return;
            }
            if (source.sourceFieldId) {
              refs[source.sourceFieldId] = [
                ...(refs[source.sourceFieldId] || []),
                {
                  fileReference: {
                    id: source.id,
                    originalName: source.originalName,
                    documentType: source.documentType,
                    uploadDate: source.uploadDate,
                    fileSize: source.fileSize,
                    mimeType: source.mimeType,
                    filePath: source.filePath || "",
                    sourceFieldId: source.sourceFieldId,
                    isNew: false,
                    indexStatus: source.indexStatus,
                    indexError: source.indexError,
                  },
                },
              ];
            }
          });
        }

        _setLoadedDoc(doc);
        setSelectedTranscripts(selected);
        setFormData({
          title: doc.Title,
          inputs: doc.inputs || {},
          sources: {},
        });
        setFileReferences(refs);
      } catch (err) {
        if (mounted) {
          console.error("Error loading document:", err);
          setDocumentError("Failed to load document");
        }
      } finally {
        if (mounted) setDocumentLoading(false);
      }
    };

    loadDocument();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount, getDocument is stable
  }, []);

  const saveTranscriptSelection = useCallback(
    async (sourceFieldId: number, transcript: Transcript | null) => {
      if (!currentDocumentId || !workspace?.id) {
        setError("Save the document before selecting a transcript source.");
        return;
      }
      setTranscriptSelectionSaving((current) => ({
        ...current,
        [sourceFieldId]: true,
      }));
      try {
        await authFetch(
          `/api/workspace/meridian/documents/${currentDocumentId}/transcript-source?workspace_id=${encodeURIComponent(workspace.id)}`,
          "save transcript source selection",
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              source_field_id: sourceFieldId,
              transcript_id: transcript?.transcript_id ?? null,
            }),
          },
        );
        setSelectedTranscripts((current) => ({
          ...current,
          [sourceFieldId]: transcript,
        }));
        setFileReferences((current) => {
          const updated = { ...current };
          delete updated[sourceFieldId];
          return updated;
        });
        setValidationErrors((current) => {
          const updated = { ...current };
          delete updated[sourceFieldId];
          return updated;
        });
      } catch (saveError) {
        console.error("Failed to save transcript source selection", saveError);
        setError(
          "Failed to select this transcript. Check that it is completed and readable.",
        );
      } finally {
        setTranscriptSelectionSaving((current) => ({
          ...current,
          [sourceFieldId]: false,
        }));
      }
    },
    [authFetch, currentDocumentId, workspace?.id],
  );

  const renderInputFields = React.useMemo(() => {
    if (!documentType?.document_type_inputs) return null;

    return documentType.document_type_inputs.map((input) => {
      const value = String(formData.inputs[input.Name] || "");

      return (
        <FormControl key={input.InputFieldID}>
          <FormLabel>
            {input.Label}
            {input.IsRequired && <span style={{ color: "#c41c1c" }}> *</span>}
          </FormLabel>
          {renderInput(input, value, setFormData, !formControlsEnabled)}
          {input.HelpText && (
            <Typography
              level="body-sm"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              {input.HelpText}
            </Typography>
          )}
          {validationErrors[input.InputFieldID!] && (
            <Box
              component="label"
              sx={{ mt: 0.5, display: "block", color: "red" }}
            >
              {validationErrors[input.InputFieldID!]}
            </Box>
          )}
        </FormControl>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- documentType and formData accessed via specific properties
  }, [
    documentType?.document_type_inputs,
    formData.inputs,
    formControlsEnabled,
    validationErrors,
  ]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !sourceUploadEnabled ||
      hasActiveSourceDeletions ||
      hasActiveTranscriptSelection
    ) {
      return;
    }
    if (
      !transcriptsEnabled &&
      documentType?.document_type_sources?.some(
        (source) => source.DocumentType === "Transcript",
      )
    ) {
      setError(
        "This document has a Transcript source, but Meridian transcripts are disabled for this workspace.",
      );
      return;
    }
    if (!currentDocumentId) {
      setError("No document ID available");
      return;
    }

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      // Add null checks for fileReferences
      const safeFileReferences = fileReferences || {};
      const sourceDocuments = Object.values(safeFileReferences)
        .filter((refs) => Array.isArray(refs))
        .flat()
        .filter((ref) => ref && ref.fileReference)
        .map((ref) => ({
          fileReference: ref.fileReference,
        }));

      const updateData = {
        documentId: currentDocumentId,
        documentTypeId,
        title: formData.title,
        inputs: formData.inputs || {},
        workspace_id: workspace?.id || "",
        sourceDocuments: sourceDocuments,
      };

      await updateDocument(updateData);
      const updatedDocument = await getDocument(
        currentDocumentId,
        workspace?.id || "",
      );
      onDocumentUpdated?.(updatedDocument);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      setError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitialSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError("Document title is required");
      return;
    }

    try {
      const response = await createDocument({
        documentTypeId,
        title: formData.title,
        inputs: formData.inputs,
        sourceDocuments: [],
        workspace_id: workspace?.id || "",
      });

      // Navigate to the edit page with proper workspace context
      // The API returns { document: { DocumentID: ... } } structure
      const documentId =
        (response as any).document?.DocumentID || response.DocumentID;
      navigate(
        `/workspace/${workspace?.id}/meridian/documents/edit/${documentId}`,
        {
          replace: true,
        },
      );
      window.location.reload();
    } catch (error) {
      // Log the raw cause for debugging; show a generic message so internal/
      // server details aren't exposed to the user.
      console.error("Error creating document:", error);
      setError("Couldn't create the document. Please try again.");
    }
  };

  const handleGenerate = async (shouldContinue: boolean = false) => {
    try {
      if (currentDocumentId) {
        if (
          isSaving ||
          isGenerationActive ||
          isGenerating ||
          isStartingGeneration ||
          isRegeneratingSection ||
          hasActiveSourceDeletions
        ) {
          return;
        }

        const hasActiveSourceUploads = Object.values(
          activeUploadsBySource,
        ).some(Boolean);
        if (hasActiveSourceUploads) {
          setError(
            "Please wait for all source documents to finish uploading before generating.",
          );
          return;
        }

        if (
          !transcriptsEnabled &&
          documentType?.document_type_sources?.some(
            (source) => source.DocumentType === "Transcript",
          )
        ) {
          setError(
            "Generation is unavailable because Meridian transcripts are disabled for this workspace.",
          );
          return;
        }

        // Validate required fields before generation
        const newValidationErrors: Record<string, string> = {};

        // Check required document_type_inputs
        if (documentType?.document_type_inputs) {
          documentType.document_type_inputs.forEach((input) => {
            if (input.IsRequired) {
              const value = formData.inputs[input.Name];
              if (
                !value ||
                (typeof value === "string" && value.trim() === "")
              ) {
                newValidationErrors[input.InputFieldID!] =
                  `${input.Label} is required`;
              }
            }
          });
        }

        // Check required document_type_sources
        if (documentType?.document_type_sources) {
          documentType.document_type_sources.forEach((source) => {
            if (source.IsRequired) {
              const hasSource =
                source.DocumentType === "Transcript"
                  ? Boolean(selectedTranscripts[source.SourceFieldID])
                  : (fileReferences[source.SourceFieldID] || []).length > 0;
              if (!hasSource) {
                newValidationErrors[source.SourceFieldID] =
                  `${source.Label} is required`;
              }
            }
            // Check source indexing status
            const files = fileReferences[source.SourceFieldID] || [];
            files.forEach((ref) => {
              const status = ref.fileReference.indexStatus;
              if (status === "failed") {
                newValidationErrors[source.SourceFieldID] =
                  `${source.Label}: Source indexing failed. Retry or remove the failed file.`;
              } else if (status === "pending" || status === "indexing") {
                newValidationErrors[source.SourceFieldID] =
                  `${source.Label}: Source is still being indexed. Please wait.`;
              }
            });
          });
        }

        // Update validation errors state
        setValidationErrors(newValidationErrors);

        // If there are validation errors, display them and stop generation
        const allErrors = [...Object.values(newValidationErrors)];
        if (allErrors.length > 0) {
          return;
        }

        // Clear any previous errors
        setValidationErrors({});
        setError(null);

        // Give immediate feedback and block re-entry: the document is saved and
        // refetched (two awaited round-trips) before the generation stream
        // starts, and this flag drives the disabled buttons + spinner so the
        // user can't double-click into a desynced generation state.
        setIsStartingGeneration(true);
        try {
          // Add null checks for fileReferences
          const safeFileReferences = fileReferences || {};
          const sourceDocuments = Object.values(safeFileReferences)
            .filter((refs) => Array.isArray(refs))
            .flat()
            .filter((ref) => ref && ref.fileReference)
            .map((ref) => ({
              fileReference: ref.fileReference,
            }));

          await updateDocument({
            documentId: currentDocumentId,
            documentTypeId,
            title: formData.title,
            inputs: formData.inputs || {},
            workspace_id: workspace?.id || "",
            sourceDocuments: sourceDocuments,
          });
          const updatedDocument = await getDocument(
            currentDocumentId,
            workspace?.id || "",
          );
          onDocumentUpdated?.(updatedDocument);

          // Start the generation. onGenerate already routes to startGeneration
          // in the parent, so we must not also call the startGeneration prop
          // here -- doing so fired it twice and relied on a fragile guard.
          onGenerate(shouldContinue);
        } finally {
          setIsStartingGeneration(false);
        }
      }
    } catch (err) {
      // Keep the raw cause in the console only; the on-screen message stays
      // generic so internal/server details aren't exposed to the user.
      console.error("Failed to start generation:", err);
      setError("Couldn't start generation. Please try again.");
    }
  };

  // Add handleDeleteSourceDocument function
  const handleDeleteSourceDocument = async (sourceDocumentId: number) => {
    setActiveSourceDeletionCount((count) => count + 1);
    try {
      await deleteSourceDocument(sourceDocumentId, workspace?.id || "");

      // Update local state to remove the file
      setFileReferences((prev) => {
        const updatedRefs = { ...prev };
        // Iterate through all source fields
        Object.keys(updatedRefs).forEach((sourceFieldId: string) => {
          updatedRefs[parseInt(sourceFieldId)] = updatedRefs[
            parseInt(sourceFieldId)
          ].filter(
            (ref: DocumentSourceReference) =>
              ref.fileReference.id !== sourceDocumentId,
          );
          // If the array is empty, delete the key
          if (updatedRefs[parseInt(sourceFieldId)].length === 0) {
            delete updatedRefs[parseInt(sourceFieldId)];
          }
        });
        return updatedRefs;
      });
    } catch (error) {
      console.error("Error deleting source document:", error);
      setError("Failed to delete source document");
    } finally {
      setActiveSourceDeletionCount((count) => Math.max(0, count - 1));
    }
  };

  const handleReindexSourceDocument = useCallback(
    async (sourceDocumentId: number) => {
      if (!formControlsEnabled) {
        return;
      }

      try {
        // Set status to indexing immediately for UI feedback
        setFileReferences((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            updated[parseInt(key)] = updated[parseInt(key)].map((ref) =>
              ref.fileReference.id === sourceDocumentId
                ? {
                    ...ref,
                    fileReference: {
                      ...ref.fileReference,
                      indexStatus: "indexing" as const,
                      indexError: null,
                    },
                  }
                : ref,
            );
          });
          return updated;
        });

        const result = await authFetch(
          `/api/workspace/meridian/documents/source-documents/${sourceDocumentId}/reindex?workspace_id=${workspace?.id}`,
          "reindex source document",
          { method: "POST" },
        );

        // Update local state with result
        setFileReferences((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            updated[parseInt(key)] = updated[parseInt(key)].map((ref) =>
              ref.fileReference.id === sourceDocumentId
                ? {
                    ...ref,
                    fileReference: {
                      ...ref.fileReference,
                      indexStatus: result.index_status,
                      indexError:
                        result.index_status === "failed"
                          ? (result.errors || []).join("; ") ||
                            "Indexing failed"
                          : null,
                    },
                  }
                : ref,
            );
          });
          return updated;
        });
      } catch (err) {
        console.error("Failed to reindex source document:", err);
        setError("Failed to re-index source document");
      }
    },
    [authFetch, formControlsEnabled, workspace?.id],
  );

  // Compute whether any source has a blocking index status
  const hasIndexingIssue = Object.values(fileReferences).some((refs) =>
    refs.some((ref) => {
      const s = ref.fileReference.indexStatus;
      return s === "failed" || s === "pending" || s === "indexing";
    }),
  );
  const hasActiveSourceUploads =
    Object.values(activeUploadsBySource).some(Boolean) ||
    hasActiveTranscriptSelection;
  const generationActionsDisabled = shouldDisableGenerationActions({
    isSaving,
    isGenerationActive,
    isGenerating,
    isStartingGeneration,
    isRegeneratingSection,
    hasActiveSourceUploads,
    hasActiveSourceDeletions,
    hasIndexingIssue,
  });
  const generationActionDisabledReason = isSaving
    ? "Wait for document changes to finish saving"
    : isGenerationActive
      ? "Document generation is already in progress"
      : isGenerating || isStartingGeneration
        ? "Document generation is already in progress"
        : isRegeneratingSection
          ? "Wait for section regeneration to finish"
          : hasActiveSourceDeletions
            ? "Wait for source document deletion to finish"
            : hasActiveSourceUploads
              ? "Wait for all source documents to finish uploading or selecting"
              : hasIndexingIssue
                ? "Resolve source indexing issues before generating"
                : "";

  // Source uploads/deletions and document saves all rewrite the document the
  // generation view is rendering, so report them upward to gate per-section
  // regeneration for as long as they are in flight.
  const sectionRegenerationBlockedReason = getSectionRegenerationBlockedReason({
    isSavingDocument: isSaving,
    hasActiveSourceUploads,
    hasActiveSourceDeletions,
  });

  useEffect(() => {
    onSectionRegenerationBlockChange?.(sectionRegenerationBlockedReason);
  }, [onSectionRegenerationBlockChange, sectionRegenerationBlockedReason]);

  useEffect(
    () => () => {
      onSectionRegenerationBlockChange?.("");
    },
    [onSectionRegenerationBlockChange],
  );

  // Show error if either fails to load
  if (documentTypeError || documentError) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        {documentTypeError || documentError}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert color="success" sx={{ mb: 2 }}>
          Document saved successfully!
        </Alert>
      )}

      <Card>
        <CardContent>
          {isPanelLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading />
            </Box>
          ) : isNewDocument ? (
            // Initial document creation form
            <form onSubmit={handleInitialSave}>
              <Stack spacing={3}>
                <Typography level="h2">
                  Create New {documentType?.Name}
                </Typography>
                <Box>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Title
                  </Typography>
                  <FormControl>
                    <FormLabel>Document Title</FormLabel>
                    <Input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      required
                      placeholder="Enter document title"
                    />
                  </FormControl>
                </Box>

                <Button type="submit" color="primary">
                  Create Document
                </Button>
              </Stack>
            </form>
          ) : (
            // Full configuration form
            <form onSubmit={handleSave}>
              <Stack spacing={3}>
                {/* Title Section */}
                <Box>
                  <Typography level="h3" sx={{ mb: 2 }}>
                    Title
                  </Typography>
                  <FormControl>
                    <FormLabel>Document Title</FormLabel>
                    <Input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      disabled={!formControlsEnabled}
                      required
                      placeholder="Enter document title"
                    />
                  </FormControl>
                </Box>

                {/* Input Fields Section */}
                {documentType?.document_type_inputs &&
                  documentType.document_type_inputs.length > 0 && (
                    <Box>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Document Details
                      </Typography>
                      <Stack spacing={2}>{renderInputFields}</Stack>
                    </Box>
                  )}

                {/* Source Documents Section */}
                {documentType?.document_type_sources &&
                  documentType.document_type_sources.length > 0 && (
                    <Box>
                      <Typography level="h3" sx={{ mb: 2 }}>
                        Source Documents
                      </Typography>
                      <Stack spacing={2}>
                        {documentType.document_type_sources.map((source) => (
                          <FormControl key={source.SourceFieldID}>
                            <FormLabel>
                              {source.Label}
                              {source.IsRequired && (
                                <span style={{ color: "#c41c1c" }}> *</span>
                              )}
                            </FormLabel>
                            {source.DocumentType === "Transcript" ? (
                              transcriptsEnabled ? (
                                <>
                                  <TranscriptSourcePicker
                                    transcripts={availableTranscripts}
                                    value={
                                      selectedTranscripts[
                                        source.SourceFieldID
                                      ] || null
                                    }
                                    disabled={
                                      !formControlsEnabled ||
                                      Boolean(
                                        transcriptSelectionSaving[
                                          source.SourceFieldID
                                        ],
                                      )
                                    }
                                    loading={
                                      transcriptsLoading ||
                                      Boolean(
                                        transcriptSelectionSaving[
                                          source.SourceFieldID
                                        ],
                                      )
                                    }
                                    onSearchChange={setTranscriptSearch}
                                    onSelect={(transcript) =>
                                      void saveTranscriptSelection(
                                        source.SourceFieldID,
                                        transcript,
                                      )
                                    }
                                  />
                                  {transcriptsLoadError ? (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography
                                        level="body-xs"
                                        sx={{ color: "danger.500" }}
                                      >
                                        Could not load transcripts.
                                      </Typography>
                                      <Button
                                        size="sm"
                                        variant="plain"
                                        onClick={() =>
                                          setTranscriptsRetryCount(
                                            (count) => count + 1,
                                          )
                                        }
                                      >
                                        Retry
                                      </Button>
                                    </Stack>
                                  ) : (
                                    !transcriptsLoading &&
                                    availableTranscripts.length === 0 && (
                                      <Typography
                                        level="body-xs"
                                        sx={{ color: "text.tertiary" }}
                                      >
                                        No completed transcripts are available
                                        in this workspace.
                                      </Typography>
                                    )
                                  )}
                                </>
                              ) : (
                                <Alert color="warning">
                                  This transcript source is unavailable because
                                  Meridian transcripts are disabled for this
                                  workspace.
                                </Alert>
                              )
                            ) : (
                              <FileUploader
                                ref={fileUploaderRef}
                                disabled={!formControlsEnabled}
                                label={
                                  formControlsEnabled
                                    ? "Drag and drop files here, or click to select files"
                                    : "Source documents can be uploaded before generation starts or after it finishes"
                                }
                                documentType={source.DocumentType}
                                uploadEndpoint="/api/workspace/meridian/documents/upload/source-document"
                                onFileReferenceReceived={(fileRef) =>
                                  handleFileReferenceReceived(
                                    source.SourceFieldID,
                                    fileRef,
                                  )
                                }
                                onUploadError={handleUploadError}
                                onUploadActivityChange={(isActive) =>
                                  handleUploadActivityChange(
                                    source.SourceFieldID,
                                    isActive,
                                  )
                                }
                                existingFiles={(
                                  fileReferences[source.SourceFieldID] || []
                                ).map((ref) => ({
                                  id: ref.fileReference.id || 0,
                                  fileName: ref.fileReference.originalName,
                                  fileSize: ref.fileReference.fileSize,
                                  url: ref.fileReference.filePath,
                                  documentType: ref.fileReference.documentType,
                                  indexStatus: ref.fileReference.indexStatus,
                                  indexError: ref.fileReference.indexError,
                                }))}
                                onDeleteFile={(id) =>
                                  handleDeleteSourceDocument(id)
                                }
                                onReindexFile={handleReindexSourceDocument}
                                metadata={{
                                  sourceFieldId: source.SourceFieldID,
                                  documentId,
                                }}
                                requireFileReference={true}
                                maxSize={20 * 1024 * 1024}
                                accept={{
                                  "application/pdf": [".pdf"],
                                  "application/msword": [".doc"],
                                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                    [".docx"],
                                }}
                              />
                            )}
                            {source.HelpText && (
                              <Typography
                                level="body-sm"
                                sx={{ mt: 0.5, color: "text.secondary" }}
                              >
                                {source.HelpText}
                              </Typography>
                            )}
                            {validationErrors[source.SourceFieldID!] && (
                              <Box
                                component="label"
                                sx={{ mt: 0.5, display: "block", color: "red" }}
                              >
                                {validationErrors[source.SourceFieldID!]}
                              </Box>
                            )}
                          </FormControl>
                        ))}
                      </Stack>
                    </Box>
                  )}

                {/* Action Buttons */}
                <Stack
                  direction="row"
                  useFlexGap
                  flexWrap="wrap"
                  spacing={2}
                  sx={{
                    "& > *": {
                      flex: "1 1 12rem",
                      minWidth: 0,
                    },
                    "& button": {
                      width: "100%",
                      minWidth: 0,
                      textAlign: "center",
                      whiteSpace: "normal",
                    },
                  }}
                >
                  {/* Save Changes - Always visible */}
                  <Button
                    type="submit"
                    color={isSaving || saveSuccess ? "success" : "neutral"}
                    variant="outlined"
                    disabled={
                      isSaving ||
                      !sourceUploadEnabled ||
                      hasActiveSourceDeletions ||
                      hasActiveTranscriptSelection
                    }
                    startDecorator={
                      isSaving ? (
                        <CircularProgress size="sm" />
                      ) : saveSuccess ? (
                        <CheckIcon />
                      ) : (
                        <SaveIcon />
                      )
                    }
                  >
                    {isSaving
                      ? "Saving..."
                      : saveSuccess
                        ? "Saved!"
                        : "Save Changes"}
                  </Button>

                  {/* Generation Buttons */}
                  {!isGenerating && !isStartingGeneration && (
                    <>
                      {/* Initial Generate Document button - only when no sections exist */}
                      {!hasExistingSections && (
                        <Tooltip title={generationActionDisabledReason}>
                          <span>
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => handleGenerate(false)}
                              disabled={generationActionsDisabled}
                              startDecorator={<PlayArrowIcon />}
                            >
                              Generate Document
                            </Button>
                          </span>
                        </Tooltip>
                      )}

                      {/* Continue Generation - when there are sections but generation isn't complete */}
                      {hasExistingSections && !generationState?.isComplete && (
                        <Tooltip title={generationActionDisabledReason}>
                          <span>
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => handleGenerate(true)}
                              disabled={generationActionsDisabled}
                              startDecorator={<PlayArrowIcon />}
                            >
                              Continue Generation
                            </Button>
                          </span>
                        </Tooltip>
                      )}

                      {/* Regenerate Document - when there are any existing sections */}
                      {hasExistingSections && documentId && (
                        <Tooltip title={generationActionDisabledReason}>
                          <span>
                            <Button
                              type="button"
                              color="neutral"
                              variant="outlined"
                              onClick={() => handleGenerate(false)}
                              disabled={generationActionsDisabled}
                              startDecorator={<RefreshIcon />}
                            >
                              Regenerate Document
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  )}

                  {/* Loading state -- also covers the gap between clicking and
                      the generation stream actually starting */}
                  {(isGenerating || isStartingGeneration) && (
                    <Button
                      color="neutral"
                      variant="outlined"
                      disabled
                      startDecorator={<CircularProgress size="sm" />}
                    >
                      {isGenerating ? "Generating..." : "Starting..."}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ConfigPanel;
